import { STANDALONE_LINK_CLASS } from "@/components/layout/standalone-link";
import { SupportWhatsAppLink } from "@/components/support/SupportWhatsAppLink";
import { Text } from "@medusajs/ui";
import type { ComponentProps } from "react";
import { Link } from "@tanstack/react-router";

interface StandaloneScreenFooterProps {
  /** The signed-in salon, for the support message's greeting. */
  profile: ComponentProps<typeof SupportWhatsAppLink>["profile"];
}

/**
 * The way out of a screen that sits outside the app shell.
 *
 * `/activate` and `/onboarding` are full-page steps with no header, and while
 * the address step is on (`ADDRESS_ONBOARDING_ENABLED` in `first-login/gate.ts`)
 * the gate sends every other signed-in route back to `/onboarding` until an
 * address is saved. A salon whose save keeps failing would otherwise have no
 * way to leave or to say so. This is that way: sign out, or message us.
 *
 * Quiet on purpose - a footnote under the card, not a competing action.
 */
export function StandaloneScreenFooter({
  profile
}: StandaloneScreenFooterProps) {
  return (
    <Text size="small" className="text-ui-fg-muted mt-6 text-center">
      Need a hand?{" "}
      {/* No text separator: a "·" in the text flow can be stranded at the end of
          a line when the card is narrow (/activate is ~300px). The gap spaces
          the actions and each is a non-breaking unit, so the line breaks
          between the two and nothing dangles. */}
      <span className="inline-flex flex-wrap justify-center gap-x-3">
        <span className="whitespace-nowrap">
          <SupportWhatsAppLink
            profile={profile}
            className={STANDALONE_LINK_CLASS}
          >
            Message us on WhatsApp
          </SupportWhatsAppLink>
        </span>
        <span className="whitespace-nowrap">
          {/* `preload={false}`: /logout ends the session, so it must never be
              fetched ahead of a click (as in the app header's menu). */}
          <Link to="/logout" preload={false} className={STANDALONE_LINK_CLASS}>
            Sign out
          </Link>
        </span>
      </span>
    </Text>
  );
}
